import { apiAuthFetch } from "@/lib/authFetch";
import { appendLocParam } from "@/lib/i18n";
import i18n from "@/lib/i18n";
import { usePdfDocumentStore, loadPdfFromArrayBuffer } from "@/components/pdf/pdfDocumentStore";
import { getLiveThreadId } from "@/components/assistant";
import { usePdfErrorStore } from "@/lib/stores/pdfErrorStore";
import { ClientLogger } from "@/lib/clientLogger";
import { parseFilenameFromResponse } from "@/lib/utils";
import type { RemoteThreadDocument } from "@/components/custom/providers/customThreadListProvider";

// ─── GUID validation ─────────────────────────────────────────────────────────

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// ─── PDF magic-byte guard ────────────────────────────────────────────────────

// PDFs always start with the literal byte sequence "%PDF-" (0x25 0x50 0x44 0x46 0x2D).
// The backend now validates this before returning bytes; the SPA guard is defence-in-depth
// against a regression that ships 200-OK + non-PDF bytes (HTML error page, empty body).
function hasPdfMagicBytes(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < 5) return false;
  const v = new Uint8Array(buffer, 0, 5);
  return v[0] === 0x25 && v[1] === 0x50 && v[2] === 0x44 && v[3] === 0x46 && v[4] === 0x2D;
}

// ─── Case document fetch ──────────────────────────────────────────────────────

export async function fetchCaseDocuments(caseId: string, docIds: string[]): Promise<RemoteThreadDocument[]> {
  if (!GUID_RE.test(caseId)) return [];

  // Empty docIds → ask the endpoint for the full document list of the case
  // (used by the empty-state case-only suggestions to auto-attach all docs).
  // Drop the trailing `?` in that branch so appendLocParam emits a clean
  // `?loc=…` rather than `?&loc=…`.
  const qs = docIds.length > 0 ? `?${docIds.map(id => `docIds=${id}`).join("&")}` : "";
  const response = await apiAuthFetch(appendLocParam(`api/cases/${caseId}/documents${qs}`));

  if (!response.ok) {
    ClientLogger.error("Failed to fetch case documents", {
      component: "thread/fetchCaseDocuments", metadata: { caseId, status: response.status },
    });
    throw new Error(i18n.t("error.code.service_unavailable"));
  }

  const documents: RemoteThreadDocument[] = await response.json();
  // Every doc returned by this endpoint is, by construction, from `caseId`.
  // Stamp it so the popover can group without a second round-trip.
  return documents.map(d => ({ ...d, caseId }));
}

// ─── Case info cache ──────────────────────────────────────────────────────────

// Session-lifetime cache for case-info lookups (case number + name) used by the
// composer CasePill (two-row display, since Phase 4 of the 2026-05-19 composer
// overhaul). Cache holds the structured object; `fetchCaseName` is a thin wrapper
// for callers that only need the name. Names + numbers rarely change inside a
// single tab; reload drops the cache, which is the right default.

export type CaseInfo = {
  /** Human "12345/2025" identifier. Nullable — Praetor doesn't guarantee one. */
  caseNumber: string | null;
  /** Descriptive title (e.g. "Smetana v. Burda"). */
  name: string | null;
};

const caseInfoCache = new Map<string, Promise<CaseInfo | null>>();

export async function fetchCaseInfo(caseId: string): Promise<CaseInfo | null> {
  if (!GUID_RE.test(caseId)) return null;
  const cached = caseInfoCache.get(caseId);
  if (cached) return cached;
  const p = apiAuthFetch(`api/cases/${caseId}`)
    .then(async r => {
      if (!r.ok) return null;
      const body = await r.json() as { name?: string | null; caseNumber?: string | null };
      const name = typeof body?.name === "string" && body.name.length > 0 ? body.name : null;
      const caseNumber = typeof body?.caseNumber === "string" && body.caseNumber.length > 0 ? body.caseNumber : null;
      if (name === null && caseNumber === null) return null;
      return { caseNumber, name };
    })
    .catch(() => null);
  caseInfoCache.set(caseId, p);
  return p;
}

export async function fetchCaseName(caseId: string): Promise<string | null> {
  const info = await fetchCaseInfo(caseId);
  return info?.name ?? null;
}

// ─── In-flight document fetch registry ───────────────────────────────────────
// Tracks AbortControllers for every in-flight openDocumentInSidebar fetch so
// they can be cancelled when:
//   • the user closes the loading tab (closeDocument fires)
//   • the user switches threads (McpThreadIdSync calls abortAllInFlightDocFetches)
//   • the panel is wiped any other way (closeOtherDocuments, closeAllDocuments)
//
// The Zustand subscription below auto-aborts whenever a doc is removed from
// the store, so the only callers that need to know about this registry are
// (a) openDocumentInSidebar (registers), (b) the thread-switch hook (clears
// the lot up-front, before closeAllDocuments removes them from the store).
const inFlightDocFetches = new Map<string, AbortController>();

export function abortInFlightDocFetch(docId: string) {
  const ac = inFlightDocFetches.get(docId);
  if (ac) {
    ac.abort();
    inFlightDocFetches.delete(docId);
  }
}

export function abortAllInFlightDocFetches() {
  for (const [, ac] of inFlightDocFetches) ac.abort();
  inFlightDocFetches.clear();
}

// Auto-abort fetches whose docs were removed from the store by any path
// (closeDocument, closeOtherDocuments, closeAllDocuments). Avoids coupling the
// store to this file (which would create a circular import).
usePdfDocumentStore.subscribe((state, prevState) => {
  if (state.documents === prevState.documents) return;
  const currentIds = new Set(state.documents.map((d) => d.id));
  for (const prev of prevState.documents) {
    if (!currentIds.has(prev.id)) abortInFlightDocFetch(prev.id);
  }
});

// ─── Document sidebar openers ─────────────────────────────────────────────────

export async function openDocumentInSidebar(
  documentId: string,
  page?: number,
  originatingThreadId?: string,
) {
  const store = usePdfDocumentStore.getState();

  // If already loaded (not loading), just activate and ensure panel is open.
  const existing = store.documents.find((d) => d.id === documentId);
  if (existing && !existing.loading) {
    store.setActiveDocument(documentId);
    if (!store.panelOpen) store.setPanelOpen(true);
    if (page) {
      store.setPendingNavigation(documentId, { page });
    }
    return;
  }

  // Show a loading tab immediately while fetching.
  store.addLoadingDocument(documentId, documentId, "citation");

  // Set pending navigation BEFORE loading so it's already in the store when
  // openDocument() triggers the viewer effects — avoids a race where the
  // effect runs between openDocument() and a later setPendingNavigation().
  if (page) {
    usePdfDocumentStore.getState().setPendingNavigation(documentId, { page });
  }

  // Abort any previous fetch for this docId (rapid re-click of same citation).
  abortInFlightDocFetch(documentId);
  const ac = new AbortController();
  inFlightDocFetches.set(documentId, ac);

  try {
    const response = await apiAuthFetch(`api/praetor/documents/${documentId}`, { signal: ac.signal });
    if (!response.ok) {
      ClientLogger.warn("Document sidebar fetch failed", {
        component: "openDocumentInSidebar", metadata: { documentId, status: response.status },
      });
      usePdfDocumentStore.getState().closeDocument(documentId);
      return;
    }

    const title = parseFilenameFromResponse(response, documentId);
    const data = await response.arrayBuffer();

    // After every async hop, defend against the user having moved on. The
    // store-subscribe coupling above already aborts on close, but a thread
    // switch races against the in-flight resolution: we may already have the
    // bytes in hand when the live thread changes.
    if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
      console.debug(
        "[doc-sidebar] dropping late fetch result for",
        documentId,
        "from",
        originatingThreadId,
        "(now on",
        getLiveThreadId(),
        ")"
      );
      return;
    }

    try {
      await loadPdfFromArrayBuffer(data, title, documentId);
    } catch (error) {
      ClientLogger.error("Failed to load PDF document", {
        error: error instanceof Error ? error : undefined,
        component: "openDocumentInSidebar", metadata: { documentId, title },
      });
      usePdfDocumentStore.getState().closeDocument(documentId);
      usePdfErrorStore.getState().show("documents.invalidPdf", { title });
    }
  } catch (error) {
    // AbortError is expected and benign — caller cancelled or thread switched.
    if (error instanceof DOMException && error.name === "AbortError") return;
    ClientLogger.error("Document sidebar fetch network error", {
      error: error instanceof Error ? error : undefined,
      component: "openDocumentInSidebar", metadata: { documentId },
    });
    usePdfDocumentStore.getState().closeDocument(documentId);
  } finally {
    // Only clear if this controller is still the registered one. A second
    // openDocumentInSidebar(docId, ...) would have replaced our entry already.
    if (inFlightDocFetches.get(documentId) === ac) {
      inFlightDocFetches.delete(documentId);
    }
  }
}

/**
 * Opens a generated (assistant-produced) document in the PDF sidebar.
 *
 * Mirrors openDocumentInSidebar but:
 *  - Hits /api/documents/{id}/download?format=pdf for the PDF rendition.
 *  - Stamps kind="generated" on the loaded PdfDocument; the store's mode-switch
 *    logic in openDocument wipes any existing citation tabs on takeover.
 *  - Fast-path: if the doc is already loaded (kind === "generated"), just activate
 *    and reopen the panel — avoids re-downloading bytes when the user clicks the
 *    inline DocumentBlock to reopen after closing the sidebar.
 */
export async function openGeneratedDocumentInSidebar(
  documentId: string,
  title?: string,
  originatingThreadId?: string,
  versionId?: string,
) {
  const store = usePdfDocumentStore.getState();

  const existing = store.documents.find((d) => d.id === documentId);
  if (
    existing &&
    !existing.loading &&
    existing.kind === "generated" &&
    // Only fast-path when the cached doc matches the requested version (or no
    // specific version was requested). Otherwise fall through to refetch.
    (!versionId || existing.versionId === versionId)
  ) {
    store.setActiveDocument(documentId);
    if (!store.panelOpen) store.setPanelOpen(true);
    return;
  }

  // Show a loading tab immediately while fetching.
  store.addLoadingDocument(documentId, title ?? documentId, "generated");

  abortInFlightDocFetch(documentId);
  const ac = new AbortController();
  inFlightDocFetches.set(documentId, ac);

  try {
    const versionQuery = versionId ? `&version=${encodeURIComponent(versionId)}` : "";
    const response = await apiAuthFetch(
      `/api/documents/${documentId}/download?format=pdf${versionQuery}`,
      { signal: ac.signal },
    );

    // Transient DOCX→PDF conversion failure on the backend: surface a retry-aware
    // dialog (rather than the generic "invalid PDF" toast) and offer a one-click
    // retry that re-runs this whole call.
    if (
      response.status === 503 &&
      response.headers.get("X-Conversion-Failed") === "1"
    ) {
      ClientLogger.warn("Generated document conversion service unavailable", {
        component: "openGeneratedDocumentInSidebar",
        metadata: { documentId, status: response.status },
      });
      const resolvedTitle = title ?? `document-${documentId}`;
      usePdfDocumentStore.getState().closeDocument(documentId);
      usePdfErrorStore.getState().show(
        "documents.conversionServiceUnavailable",
        { title: resolvedTitle },
        () => { void openGeneratedDocumentInSidebar(documentId, title, originatingThreadId, versionId); },
      );
      return;
    }

    if (!response.ok) {
      ClientLogger.warn("Generated document sidebar fetch failed", {
        component: "openGeneratedDocumentInSidebar", metadata: { documentId, status: response.status },
      });
      usePdfDocumentStore.getState().closeDocument(documentId);
      return;
    }

    const data = await response.arrayBuffer();

    // Defence-in-depth: the backend now validates %PDF- before returning, but if a
    // regression ever ships 200-OK + non-PDF bytes the SPA shouldn't hand them to
    // pdfjs unfiltered. Treat as the conversion-unavailable path (retry helps).
    if (!hasPdfMagicBytes(data)) {
      ClientLogger.warn("Generated document missing PDF magic bytes", {
        component: "openGeneratedDocumentInSidebar",
        metadata: { documentId, length: data.byteLength },
      });
      const resolvedTitle = title ?? `document-${documentId}`;
      usePdfDocumentStore.getState().closeDocument(documentId);
      usePdfErrorStore.getState().show(
        "documents.conversionServiceUnavailable",
        { title: resolvedTitle },
        () => { void openGeneratedDocumentInSidebar(documentId, title, originatingThreadId, versionId); },
      );
      return;
    }

    // After every async hop, defend against the user having moved on (Phase 1
    // staleness gate). Thread switches race against in-flight resolution.
    if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
      console.debug(
        "[doc-sidebar] dropping stale generated-doc fetch for",
        documentId,
        "from",
        originatingThreadId,
        "(now on",
        getLiveThreadId(),
        ")"
      );
      return;
    }

    const resolvedTitle = title ?? `document-${documentId}`;
    try {
      await loadPdfFromArrayBuffer(data, resolvedTitle, documentId, "generated", versionId);
    } catch (error) {
      ClientLogger.error("Failed to load generated PDF", {
        error: error instanceof Error ? error : undefined,
        component: "openGeneratedDocumentInSidebar", metadata: { documentId, title: resolvedTitle },
      });
      usePdfDocumentStore.getState().closeDocument(documentId);
      usePdfErrorStore.getState().show("documents.invalidPdf", { title: resolvedTitle });
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    ClientLogger.error("Generated document sidebar fetch network error", {
      error: error instanceof Error ? error : undefined,
      component: "openGeneratedDocumentInSidebar", metadata: { documentId },
    });
    usePdfDocumentStore.getState().closeDocument(documentId);
  } finally {
    if (inFlightDocFetches.get(documentId) === ac) {
      inFlightDocFetches.delete(documentId);
    }
  }
}

/**
 * Opens a staged pre-approval preview (ADR-042) in the PDF sidebar. Mirrors
 * <see cref="openGeneratedDocumentInSidebar"/> but:
 *  - Hits <c>/api/preview/{previewId}/pdf</c> for the PDF rendition — same retry-aware
 *    503 + <c>X-Conversion-Failed</c> contract as the documents endpoint.
 *  - Stamps the PdfDocument's id as the <c>previewId</c> so
 *    <c>ApprovalActionsRow</c> can match <c>activeDocumentId === pending.previewId</c>.
 *  - Stamps kind="generated" because the viewer flow is identical to a generated
 *    document; the only difference is the source URL and the ApprovalActionsRow
 *    visibility gate.
 */
export async function openPreviewInSidebar(
  previewId: string,
  title?: string,
  originatingThreadId?: string,
  documentId?: string,
) {
  const store = usePdfDocumentStore.getState();

  const existing = store.documents.find((d) => d.id === previewId);
  if (existing && !existing.loading && existing.kind === "generated") {
    store.setActiveDocument(previewId);
    if (!store.panelOpen) store.setPanelOpen(true);
    return;
  }

  store.addLoadingDocument(previewId, title ?? `preview-${previewId}`, "generated", documentId);

  abortInFlightDocFetch(previewId);
  const ac = new AbortController();
  inFlightDocFetches.set(previewId, ac);

  try {
    const response = await apiAuthFetch(`/api/preview/${previewId}/pdf`, { signal: ac.signal });

    if (
      response.status === 503 &&
      response.headers.get("X-Conversion-Failed") === "1"
    ) {
      ClientLogger.warn("Preview conversion service unavailable", {
        component: "openPreviewInSidebar",
        metadata: { previewId, status: response.status },
      });
      const resolvedTitle = title ?? `preview-${previewId}`;
      usePdfDocumentStore.getState().closeDocument(previewId);
      usePdfErrorStore.getState().show(
        "documents.conversionServiceUnavailable",
        { title: resolvedTitle },
        () => { void openPreviewInSidebar(previewId, title, originatingThreadId); },
      );
      return;
    }

    if (!response.ok) {
      ClientLogger.warn("Preview sidebar fetch failed", {
        component: "openPreviewInSidebar", metadata: { previewId, status: response.status },
      });
      usePdfDocumentStore.getState().closeDocument(previewId);
      return;
    }

    const data = await response.arrayBuffer();

    if (!hasPdfMagicBytes(data)) {
      ClientLogger.warn("Preview missing PDF magic bytes", {
        component: "openPreviewInSidebar",
        metadata: { previewId, length: data.byteLength },
      });
      const resolvedTitle = title ?? `preview-${previewId}`;
      usePdfDocumentStore.getState().closeDocument(previewId);
      usePdfErrorStore.getState().show(
        "documents.conversionServiceUnavailable",
        { title: resolvedTitle },
        () => { void openPreviewInSidebar(previewId, title, originatingThreadId); },
      );
      return;
    }

    if (originatingThreadId && originatingThreadId !== getLiveThreadId()) {
      console.debug(
        "[preview-sidebar] dropping stale preview fetch for",
        previewId,
        "from",
        originatingThreadId,
        "(now on",
        getLiveThreadId(),
        ")"
      );
      return;
    }

    const resolvedTitle = title ?? `preview-${previewId}`;
    try {
      await loadPdfFromArrayBuffer(data, resolvedTitle, previewId, "generated", previewId, documentId);
    } catch (error) {
      ClientLogger.error("Failed to load preview PDF", {
        error: error instanceof Error ? error : undefined,
        component: "openPreviewInSidebar", metadata: { previewId, title: resolvedTitle },
      });
      usePdfDocumentStore.getState().closeDocument(previewId);
      usePdfErrorStore.getState().show("documents.invalidPdf", { title: resolvedTitle });
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return;
    ClientLogger.error("Preview sidebar fetch network error", {
      error: error instanceof Error ? error : undefined,
      component: "openPreviewInSidebar", metadata: { previewId },
    });
    usePdfDocumentStore.getState().closeDocument(previewId);
  } finally {
    if (inFlightDocFetches.get(previewId) === ac) {
      inFlightDocFetches.delete(previewId);
    }
  }
}
